import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom'
import { makeStyles, styled } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { STC_ID, STRATUS_ID } from '../../services/constants';

import WorkflowButton from '../WorkflowButton';
import { TextField } from '@mui/material';
import usePagination from '@mui/material/usePagination';
import Loading from '../Loading';
import { isRelease2me, getMFPHash } from '../../services/api';
import { useLocalStorage } from '../../services/hooks/useLocalStorage';
import STCLogo from '../../img/stc.png';
import StratusLogo from '../../img/stratus.png';
import { Release2MeShortcut } from '../Release2Me';

const useStyles = makeStyles({
    function_bar: {
        background: "#E6E6E6",
        display: "flex",
        alignItems: "center",
        height: "56px"
    },
    function_text: {
        color: "#323232",
        font: "normal normal normal 22px Nimbus Sans",
        padding: "0 16px"
    },
    workflows: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignContent: "flex-start",
        margin: "auto",
        maxWidth: "864px",
        height: "358px"
    },
    paging: {
        position: "fixed",
        bottom: "32px",
        left: "50%",
        width: "auto",
        transform: "translateX(-50%)"
    },
    page_dot: {
        height: "12px",
        width: "12px",
        background: "#B4B4B4 0% 0% no-repeat padding-box",
        border: "1px solid #B4B4B4",
        opacity: "1",
        borderRadius: "50%",
        margin: "10px",
    },
    empty_workflow_container: {
        display: "flex",
        flexDirection: "row",
        alignContent: "center",
        justifyContent: "center",
        alignItems: "center",
        margin: "10%"
    },
    empty_workflow_text_container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    empty_workflow_header: {
        color: "#1b89b0",
        fontSize: "1em",
    },
    empty_workflow_text: {
        margin: "8px 0",
        textAlign: "center"
    }
})

const Workflows = (props) => {
    const tenant = props.tenant;
    const classes = useStyles();
    const [stratus] = useLocalStorage("stratus", false);
    const [release2me, setRelease2me] = useLocalStorage("release2me", false);
    const [universalPrintEnabled, setUniversalPrintEnabled] = useLocalStorage("univPrintEnabled", false);
    const [accessToken, _] = useLocalStorage('accessToken', undefined);
    const ns = stratus ? 'stratus' : 'scantrip';
    const { t } = useTranslation(ns);
    const [page, setPage] = useState(0);
    const [pages, setPages] = useState(0);
    const [wfs, setWfs] = useState(undefined);
    const workflows = props.workflows;
    const [search, setSearch] = useState('');
    const [_isLoading, setIsLoading] = useState(true);
    const history = useHistory();
    const [r2mLoaded, setR2mLoaded] = useState(false);
    // For detecting swiping
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 50;

    async function setSecureReleaseFlags() {
        let appId = STC_ID;
        if (stratus) {
            appId = STRATUS_ID;
        }
        const { hash } = await getMFPHash(appId)
        const { r2mRegistered, universalPrintEnabled } = await isRelease2me(tenant.region, tenant.id, hash, accessToken)
        setRelease2me(r2mRegistered)
        setUniversalPrintEnabled(universalPrintEnabled)
        setR2mLoaded(true)
    }

    useEffect(() => {
        setSecureReleaseFlags()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        // TODO Cache search filters to improve performance on MFPs
        if (workflows !== undefined) {
            setIsLoading(true);
            let tempWorkflows = [...workflows].filter(workflow => {
                if (!search) {
                    return true;
                }
                return workflow.name.toLowerCase().includes(search.toLowerCase());
            });
            // Add placeholders used to make it look nice
            // TODO Redo CSS so this isn't required
            let boxesToAdd = 0;
            if (tempWorkflows.length % 4 > 0) {
                boxesToAdd = 4 - (tempWorkflows.length % 4);
            }
            for (let i = 0; i < boxesToAdd; i++) {
                const placeholder = {
                    id: `PLACEHOLDER${i}`,
                    name: `PLACEHOLDER${i}`,
                    placeholder: true
                }
                tempWorkflows.push(placeholder);
            }
            // Chop into pages
            const pageCount = Math.ceil(tempWorkflows.length / 8);
            setPages(pageCount);
            if (pageCount > 1) {
                if (release2me && page === 0) {
                    tempWorkflows = tempWorkflows.slice(page * 7, Math.min((page * 7) + 7, tempWorkflows.length));
                } else {
                    tempWorkflows = tempWorkflows.slice(page * 8, Math.min((page * 8) + 8, tempWorkflows.length));
                }
            }
            setWfs(tempWorkflows);
            setIsLoading(false);
        }
    }, [page, pages, search, workflows, props.workflows, release2me]);

    // Swipe detection functions
    const onTouchStart = (event) => {
        setTouchEnd(null);
        setTouchStart(event.targetTouches[0].clientX);
    }

    const onTouchMove = (event) => {
        setTouchEnd(event.targetTouches[0].clientX);
    }

    const onTouchEnd = (_event) => {
        if (!touchStart || !touchEnd) {
            return;
        }
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        // Change pages on swipe
        // These numbers are adjusted because page is zero indexed and handlePage
        // expects it to be off by 1
        if (isLeftSwipe) {
            handlePage(null, page + 2);
        } else if (isRightSwipe) {
            handlePage(null, page);
        }
    }

    const handlePage = (_, value) => {
        if (value - 1 >= 0 && value - 1 < pages) {
            setPage(value - 1);
        }
    }

    const Paginate = () => {
        const List = styled('ul')({
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
        });
        const { items } = usePagination({
            count: pages,
            onChange: handlePage,
            page: page + 1
        });

        return (
            <nav>
                <List>
                    {items.map(({ _, type, selected, ...item }, index) => {
                        let children = null;
                        if (type === 'page') {
                            children = (
                                <button
                                    className={classes.page_dot}
                                    type="button"
                                    style={{
                                        background: selected ? '#007DA8 0% 0% no-repeat padding-box' : '#B4B4B4 0% 0% no-repeat padding-box',
                                    }}
                                    {...item}
                                >
                                </button>
                            );
                        }

                        return <li key={index}>{children}</li>
                    })}
                </List>
            </nav>
        );
    }

    const handleSearchText = (event) => {
        setSearch(event.target.value);
        setPage(0);
        setTimeout(() => {
            document.querySelector('#search-input').focus();
        }, 0);
    }

    const toolbarRender = () => {
        return (
            <div className={classes.function_bar}>
                <div className={classes.function_text}>{t('Select a Workflow')}</div>
                <TextField
                    id="search-input"
                    placeholder={t("Search")}
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={handleSearchText}
                    sx={{
                        background: '#FFFFFF',
                        border: '1px solid #707070',
                        marginLeft: 'auto',
                        marginRight: '16px',
                        width: '293px',
                        '& .MuiInputBase-root': {
                            borderRadius: '0',
                            border: 'none'
                        }
                    }}
                />
            </div>
        );
    }
    // universal print can be toggled independently of r2m feature, and both features use the R2M 'workflow' tile on this UI page
    const isR2mTileDisplayed = release2me || universalPrintEnabled
    const listRender = () => {
        const r2mAction = () => history.push('/release2me/jobs')
        const modifiedWorkflows = isR2mTileDisplayed && page === 0 ? [{id: '-1', name: 'Release2Me'}, ...wfs] : [...wfs]
        const workflowCountMinusPlaceholders = modifiedWorkflows.filter(wf => wf.placeholder !== true).length
        if (r2mLoaded && workflowCountMinusPlaceholders === 1 && props.autostart) {
            const soloWorkflow = modifiedWorkflows[0]
            if (soloWorkflow.id === '-1') {
                r2mAction()
            } else {
                props.startWfx(soloWorkflow.id)
            }
            return (
                <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
                    {toolbarRender()}
                </div>
            )
        }
        return (
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            {toolbarRender()}
            <div className={classes.workflows}>
                {modifiedWorkflows.map(workflow => {
                    if (workflow.id === '-1') {
                        return <Release2MeShortcut action={r2mAction} />
                    } else {
                        return <WorkflowButton key={workflow.id} onClick={() => props.startWfx(workflow.id)} workflow={workflow} tenant={tenant} />
                    }
                    
                })}
            </div>
            {pages > 1 ? (
                <div className={classes.paging}>
                    <Paginate />
                    </div>
                ) : ""}
            </div>
        )
    }

    const emptyRender = () => {
        // TODO See #118
        if (search) {
            return toolbarRender();
        }
        const logo = stratus ? StratusLogo : STCLogo;
        return (
            <div className={classes.empty_workflow_container}>
                <div>
                    <img src={logo} alt="Dispatcher ScanTrip Cloud" />
                </div>
                <div className={classes.empty_workflow_text_container}>
                    <h2 className={classes.empty_workflow_header}>{t('No workflows found')}</h2>
                    <div className={classes.empty_workflow_text}>
                        {t('There are currently no workflows available.')}
                    </div>
                    <div className={classes.empty_workflow_text}>
                        {t('A workflow needs to be successfully created and running for it to be listed here.')}
                    </div>
                    <div className={classes.empty_workflow_text}>
                        {t('For more information, please refer to the documentation and sample workflows.')}
                    </div>
                </div>
            </div>
        );
    }

    const loadingPlaceholderRender = () => {
        const placeholders = [
            { id: 'placeholder0' },
            { id: 'placeholder1' },
            { id: 'placeholder2' },
            { id: 'placeholder3' },
            { id: 'placeholder4' },
            { id: 'placeholder5' },
            { id: 'placeholder6' },
            { id: 'placeholder7' }
        ]
        return (
            <>
                <Loading clickFunction={props.debugHandler} />
                <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
                    {toolbarRender()}
                    <div className={classes.workflows}>
                        {placeholders.map(workflow => {
                            return <WorkflowButton key={workflow.id} onClick={() => { }} workflow={workflow} tenant={tenant} />
                        })}
                    </div>
                    {pages > 1 ? (
                        <div className={classes.paging}>
                            <Paginate />
                        </div>
                    ) : ""}
                </div>
            </>
        )
    }

    return (
        wfs?.length !== undefined ? (
            wfs.length > 0 || isR2mTileDisplayed ? listRender() : emptyRender()
        ) : (
            loadingPlaceholderRender()
        )
    )
}

export default Workflows
